import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { CARRIED, NOT_CARRIED, type ParcelCategory } from "@/lib/parcelCatalog";
import { company } from "@/lib/company";

/**
 * What WeShip can and cannot carry.
 *
 * Generated from PARCEL_CATEGORIES -- the same list the booking form enforces
 * -- rather than written out again here. A published list that disagrees with
 * what the form accepts is worse than no list: a customer reads one, books
 * against the other, and finds out at the door.
 *
 * What we carry comes first, for the same reason it does in the picker: the
 * page is read by somebody deciding whether to send something, and an opening
 * wall of refusals answers a question they did not ask.
 */
const GROUPS: { key: string; title: string; blurb: string; items: ParcelCategory[] }[] = [
  {
    key: "carried",
    title: "What we carry",
    blurb:
      "These ship as normal parcels. Where a line carries a note, that is how to pack it or what cover it has -- not a reason it cannot go.",
    items: CARRIED,
  },
  {
    key: "not-carried",
    title: "What we cannot carry",
    blurb:
      "Booking one of these is a breach of these terms. If a parcel is found to contain any of them it will be held or returned at your cost, and no claim for loss or damage will be payable.",
    items: NOT_CARRIED,
  },
];

export function Prohibited() {
  return (
    <>
      <div className="mx-auto max-w-2xl px-4 py-12 text-ink-800">
        <Link to="/" className="text-sm font-medium text-brand">
          ← {company.tradingName}
        </Link>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-ink-900">What we can carry</h1>
        <p className="mt-1 text-sm text-ink-400">
          The same list the booking form checks against, so the two cannot disagree.
        </p>

        <div className="prose-sm mt-8 space-y-8 text-[15px] leading-relaxed">
          {GROUPS.map((group) => {
            if (group.items.length === 0) return null;
            return (
              <section key={group.key}>
                <h2 className="text-lg font-semibold text-ink-900">{group.title}</h2>
                <p className="mt-2">{group.blurb}</p>
                <ul className="mt-3 list-disc space-y-1.5 pl-5">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      {item.label}
                      {item.note && <span className="block text-sm text-ink-400">{item.note}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          <section>
            <h2 className="text-lg font-semibold text-ink-900">Packaging is yours</h2>
            <p className="mt-2">
              We carry what you hand us in the packaging you hand it to us in. A parcel that is not
              packed well enough for normal courier handling — stacking, sorting, a van over a bad
              road — is not covered for damage, whatever its contents. If you are unsure, over-pack.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">Not sure?</h2>
            <p className="mt-2">
              Ask before you book, at{" "}
              <a className="text-brand" href={`mailto:${company.supportEmail}`}>
                {company.supportEmail}
              </a>
              . A question costs nothing; a parcel turned away at collection costs a day.
            </p>
          </section>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
